/**
 * The people a record board's rows refer to, as the board received them.
 *
 * A record field like a project's owner stores a bare platform user id, and the
 * person behind it need not be a member of the workspace being looked at: they
 * may have left it, or never joined it while still creating work inside it. The
 * workspace roster every other people cell resolves against therefore cannot
 * name them, and the cell falls through to an anonymous avatar.
 *
 * So the rows endpoint sends the identities with the rows, resolved org-wide,
 * and they travel to the cells through here rather than through the row data:
 * the cell is shared with the boards that hold their own rows, where the roster
 * is still the right answer. An empty directory is the honest default, and it
 * is what a custom board provides by not providing one at all.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { TrackerRowUser } from '@/entities/tracker';

const RecordUsersContext = createContext<ReadonlyMap<string, TrackerRowUser>>(new Map());

export function RecordUsersProvider({
  users,
  children,
}: {
  users: ReadonlyMap<string, TrackerRowUser>;
  children: ReactNode;
}) {
  return <RecordUsersContext.Provider value={users}>{children}</RecordUsersContext.Provider>;
}

/** Names for the ids on screen, empty outside a record board. */
export function useRecordUsers(): ReadonlyMap<string, TrackerRowUser> {
  return useContext(RecordUsersContext);
}

/**
 * One directory across every page in hand. Later pages bring people the first
 * did not, and the counted values bring people no page carries at all, so a
 * board keeps naming everyone it has ever been told about while it is open.
 */
export function useRecordUserDirectory(
  pages: ReadonlyArray<{ users?: TrackerRowUser[] }>,
): ReadonlyMap<string, TrackerRowUser> {
  return useMemo(
    () => new Map(pages.flatMap((page) => page.users ?? []).map((user) => [user.id, user])),
    [pages],
  );
}
